import { Checkbox } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import {
  asSpelled,
  CompactBar,
  CompactChipLine,
  CompactDivider,
  CompactFilters,
  CompactMenu,
  CompactPortal,
  CompactTitle,
  FilterChip,
  HelpPopover,
  PageHeader,
  SelectChip,
  SplitList,
  STICKY_BAR_TOP,
  STICKY_TABLE_TOP,
  useScrolledPast,
  useWorkToolbar,
  WorkToolbar,
  type DataTableState,
  type ICompactChip,
  WorkControls,
  type ICompactMenuItem,
  PaneControl,
  usePanePrefs
} from '@/design';
import type { IPage } from '../../api/oversight-contracts';
import type {
  IMyTask,
  IMyTasksPage,
  IMyTaskProgress,
  IWorkItemQuery,
  IWorkItemRow,
  MyTaskFlag,
  MyTaskType,
  WorkItemFlag
} from '../../api/work-item-contracts';
import { workItemsApi } from '../../api/work-items-api';
import {
  LoadError,
  PastTheEnd,
  pageOf,
  toggleRepeated,
  useAdminData,
  withUnknown
} from '../../components/admin-page';
import { ListArea, usePaneRead } from '../../components/list-area';
import {
  ALL_TASK_SIGNALS,
  MY_TASK_SIGNALS,
  useLastVisit,
  type MyTaskSignalSpec
} from '../../components/my-tasks-summary';
import type { ICardLinkState } from '../../components/participant-link';
import {
  SignalChips,
  StateCards,
  signalsOf,
  useCardVariant,
  type IStateSignal
} from '../../components/state-cards';
import { countOf } from '../../lists';
import { TEAM_PATHS, myTaskPath } from '../../paths';
import { refusalMessage } from '../../refusal';
import {
  UNCLOSED_STATES,
  WORK_ITEM_QUEUES,
  WORK_ITEM_TRIGGERS,
  isWorkItemQueue,
  triggerLabel
} from '../../work-items';
import { WorkPageHeaderActions, type IHeaderRefresh } from '../lists/work-page/header-actions';
import {
  WORK_ITEMS_MAX_PAGE,
  WorkPager,
  dropPaging,
  readItemPages,
  usePerPage,
  useWorkPaging,
  windowOf
} from '../lists/work-page/pager';
import { ParamChip } from '../sec-adv/firm-filters';
import { BulkDecisions, useQuerySelection } from './bulk-decisions';
import {
  MY_TASK_TYPES,
  MyTasksTable,
  TaskState,
  taskKey,
  taskName,
  taskPath,
  taskSurvey,
  taskTitle
} from './my-tasks-table';
import { STAFF_FIELDS_NOTE } from './staff-columns';
import { WorkItemsTable } from './work-items-table';

/**
 * My tasks (SR-222, F9's S12; R9; SR-424) — everything assigned to the viewer, in one list: their
 * compliance work items and every Lists row assigned to them (a DD call, a reinstatement, a metrics
 * update, any other list row), read in one page from survey-api's `/admin/my-tasks`, which merges and pages them and
 * counts each type. The two models stay apart (due-diligence design D6): a list row is no work
 * item; every task opens beside the list, where it is worked (`MyTasksTable`,
 * `AdminMyListTaskPage`).
 *
 * **The type is a row of cards, each with survey-api's count and its live signals** (`?type=`, the
 * URL the state, a value no card knows still sent, drawn as spelled, and survey-api's 400 said) —
 * both from the list's own read, the cards following the viewer's Compact/Detailed choice.
 *
 * **Every signal is a toggle** (`?flag=`, repeated, beside the type): survey-api narrows the list
 * to the tasks raising any flag pressed (`flag=` repeated), each said in the chips line; a signal
 * of another card opens that card with it alone. The cards' counts stay whole, and a type shows
 * only the signals it can raise. Each row is marked by its own flags and by those applied
 * (`task-marks.tsx`), and a detailed card's bar is the type's work done this cycle over its whole.
 *
 * **"New" is since the viewer's last visit on this browser** (`useLastVisit`), sent as `since`.
 *
 * **Compliance queue is the old My tasks, whole**: the work items under a fixed query —
 * `assignee=me`, the three unclosed states (an escalated item included, the owner's U14),
 * `sort=due_on` — with the queue and what raised an item as filters and the bulk decisions, since
 * only there is every row a work item. Its flags (overdue, due today, new) narrow the queue itself
 * to the items raising any of them (the work items' repeated `flag=`, `since` for new), the filters
 * and decisions kept.
 *
 * "Me" is survey-api's word for the caller, so nothing here reads who the viewer is.
 *
 * **The page scrolls, the table's header sticking** under the app bar: once the cards and the
 * list's filters have scrolled away, the compact bar (`condense.tsx`) draws them in the app bar's
 * row — the open type as a menu of the others, Compliance queue's *Filters* and the actions — and
 * the flags and filters applied on a row under it, as the list draws them under its toolbar. The
 * bar stays the page's, so a flag dropped there does not draw it anew; the list draws its parts
 * into the bar's places (`barSlots`).
 */
export function AdminMyTasksPage() {
  const [params] = useSearchParams();
  const type = params.get('type');
  // As the URL spells them: a flag survey-api does not know is still sent, and its 400 said.
  const flags = flagsOf(params);
  const visit = useLastVisit();
  const [counts, setCounts] = React.useState<Nullable<TaskCounts>>(null);
  // The header's Refresh: moved by a press, the open list and its counts read again.
  const [reread, setReread] = React.useState(0);
  const [reading, setReading] = React.useState(false);
  const [readAt, setReadAt] = React.useState<Nullable<Date>>(null);
  const { visited } = visit;
  const onCounts = React.useCallback(
    (next: TaskCounts) => {
      setCounts(next);
      setReadAt(new Date());
      visited();
    },
    [visited]
  );
  const all = counts ? Object.values(counts.counts).reduce((sum, n) => sum + n, 0) : null;
  const refresh: IHeaderRefresh = {
    reading,
    readAt,
    onClick: () => setReread((count) => count + 1)
  };
  const help = (
    <HelpPopover label="About My tasks" align="end">
      <p>
        Everything assigned to you — compliance work items, number checks, DD meetings (in-person
        interviews and follow-up calls), DD calls, reinstatements, metrics updates and Lists rows
        — in one list.
      </p>
      <p>
        Each card is a type of task with its count; its badges count the overdue calls
        and work items, those due today, the DD calls booked this week, the reinstatements
        edited this cycle or with no outreach, the metrics updates not asked yet or with no
        reply, and the tasks new since your last visit on this browser. Press a badge to
        show only those tasks, and another to show those too; press it again, or its ✕ in
        the chips line, to drop it. The counts stay as they are.
      </p>
      <p>
        Ticked work items are offered only the decisions every one of them takes: a reviewed item
        takes none, a firm move only its own.
      </p>
      <p>
        A task opens beside the list by its first icon and in a new tab by ↗; a click on the row or
        its link goes where «Opens in» says.
      </p>
      <p>
        Scrolled down, the header, the cards and the filters ride slimmed in the bar at the top of
        the window, with the table’s column names under them.
      </p>
    </HelpPopover>
  );
  // Where a task opens (*Opens in*, every work page's one choice): beside the list, or a new tab.
  const prefs = usePanePrefs();
  const typeCards = (
    <TypeCards
      type={type}
      flags={flags}
      counts={counts}
      end={<PaneControl prefs={prefs} kind="record" />}
    />
  );
  // Where the list's own controls end — the list draws it.
  const [controlsEnd, setControlsEnd] = React.useState<Nullable<HTMLElement>>(null);
  const scrolled = useScrolledPast(controlsEnd);
  const typeItems = typeMenuItems(type, counts);
  const openType = typeItems.find((item) => item.current);
  // The bar's places for the list's own parts: Compliance queue's *Filters*, and the filters
  // applied as the bar's second row — drawn while the address names one, as the list does.
  const [filtersSlot, setFiltersSlot] = React.useState<Nullable<HTMLElement>>(null);
  const [appliedSlot, setAppliedSlot] = React.useState<Nullable<HTMLElement>>(null);
  const queue = type === 'compliance' && flags.every(isWorkItemFlag);
  const applied = flags.length > 0 || (queue && (params.has('survey') || params.has('trigger')));
  const barSlots: IBarSlots = { filters: filtersSlot, applied: appliedSlot };

  return (
    <>
      <PageHeader
        title="My tasks"
        row
        actions={
          <WorkPageHeaderActions
            summary={all === null ? null : countOf(all, 'task')}
            summaryData={{ 'data-tasks-summary': '' }}
            refresh={refresh}
            help={help}
          />
        }
      />
      <CompactBar
        shown={scrolled}
        label="My tasks, compact"
        actions={<WorkPageHeaderActions summary={null} refresh={refresh} help={help} />}
        below={applied && <div ref={setAppliedSlot} className="grid min-w-0 grid-cols-[minmax(0,1fr)]" />}
      >
        <CompactTitle>My tasks</CompactTitle>
        <CompactMenu
          label="Task type"
          chip
          trigger={
            <>
              {openType?.label ?? asSpelled(type ?? '')}
              <span className="font-semibold tabular-nums">{openType?.count ?? '…'}</span>
            </>
          }
          items={typeItems}
        />
        {queue && (
          <>
            <CompactDivider />
            <span ref={setFiltersSlot} className="contents" />
          </>
        )}
        <span className="flex-1" />
        <CompactDivider />
      </CompactBar>
      <div className="grid min-w-0 gap-3 px-6 py-4">
        {typeCards}
        {type !== null && !MY_TASK_TYPES.some((entry) => entry.value === type) && (
          // A type no card knows, as the URL named it: a position, not a press.
          <span className="justify-self-start rounded-full border border-[var(--color-teal)] px-3 py-1 text-sm">
            {asSpelled(type)}
          </span>
        )}
        {queue ? (
          <ComplianceTasks
            flags={flags as WorkItemFlag[]}
            since={visit.since}
            reread={reread}
            onCounts={onCounts}
            onReading={setReading}
            controlsEnd={setControlsEnd}
            barSlots={barSlots}
          />
        ) : (
          <AllTasks
            key={`${type ?? 'all'}|${flags.join(',')}`}
            type={type}
            flags={flags}
            since={visit.since}
            reread={reread}
            onCounts={onCounts}
            onReading={setReading}
            controlsEnd={setControlsEnd}
            barSlots={barSlots}
          />
        )}
      </div>
    </>
  );
}

/** The compact bar's places for the list's parts, while it shows: its *Filters* and its row of
 *  the filters applied. */
interface IBarSlots {
  filters: Nullable<HTMLElement>;
  applied: Nullable<HTMLElement>;
}

/** The types as the compact bar's menu — All, then each type — each its card's link and count,
 *  "…" until read. */
function typeMenuItems(type: Nullable<string>, counts: Nullable<TaskCounts>): ICompactMenuItem[] {
  const all = counts ? Object.values(counts.counts).reduce((sum, n) => sum + n, 0) : null;
  return [{ value: null, label: 'All' }, ...MY_TASK_TYPES].map((entry) => {
    const count = entry.value === null ? all : counts?.counts[entry.value];
    return {
      key: entry.value ?? 'all',
      label: entry.label,
      to: { pathname: TEAM_PATHS.tasks, search: entry.value === null ? '' : `?type=${entry.value}` },
      current: type === entry.value,
      count: count?.toLocaleString('en-US') ?? '…'
    };
  });
}

/** What a read of My tasks says of every type: its counts, signals and progress. */
type TaskCounts = Pick<IMyTasksPage, 'counts' | 'signals' | 'progress'>;

const HINTS: Record<MyTaskType | 'all', string> = {
  all: 'all your tasks',
  compliance: 'review and decide',
  identity_check: 'numbers to confirm',
  meeting: 'interviews to hold',
  dd_call: 'calls to make',
  reinstatement: 'decide who comes back',
  metrics: 'figures to chase',
  list: 'rows assigned to you'
};

/** Each type's words in a signal's name, one and many. */
const NOUNS: Record<MyTaskType | 'all', [string, string]> = {
  all: ['task', 'tasks'],
  compliance: ['compliance work item', 'compliance work items'],
  identity_check: ['number check', 'number checks'],
  meeting: ['DD meeting', 'DD meetings'],
  dd_call: ['DD call', 'DD calls'],
  reinstatement: ['reinstatement', 'reinstatements'],
  metrics: ['metrics update', 'metrics updates'],
  list: ['Lists row', 'Lists rows']
};

/** Each flag in words: how a signal's button names its rows (`noun` the type's), the chip, and
 *  what no task is in the empty list. */
const FLAG_SAID: Record<MyTaskFlag, { show: (noun: string) => string; chip: string; none: string }> = {
  overdue: { show: (noun) => `overdue ${noun}`, chip: 'overdue', none: 'is overdue' },
  due_today: { show: (noun) => `${noun} due today`, chip: 'due today', none: 'is due today' },
  new: { show: (noun) => `new ${noun}`, chip: 'new since your last visit', none: 'is new since your last visit' },
  calls_this_week: {
    show: (noun) => `${noun} this week`,
    chip: 'calls this week',
    none: 'is a call booked this week'
  },
  no_outreach: {
    show: (noun) => `${noun} with no outreach`,
    chip: 'no outreach',
    none: 'is waiting on a first outreach'
  },
  edited_this_cycle: {
    show: (noun) => `${noun} edited this cycle`,
    chip: 'edited this cycle',
    none: 'was edited this cycle'
  },
  not_asked: { show: (noun) => `${noun} not asked yet`, chip: 'not asked yet', none: 'is not asked yet' },
  asked_no_reply: {
    show: (noun) => `${noun} asked, with no reply`,
    chip: 'asked, no reply',
    none: 'is asked and without a reply'
  }
};

/** A flag the URL names as survey-api's words, or as spelled where this side knows none. */
function flagSaid(flag: string): (typeof FLAG_SAID)[MyTaskFlag] {
  const spelled = asSpelled(flag);
  return (
    FLAG_SAID[flag as MyTaskFlag] ?? {
      show: (noun: string) => `${noun} ${spelled}`,
      chip: spelled,
      none: `is ${spelled}`
    }
  );
}

/** The flags the URL names, once each, as spelled. */
function flagsOf(params: URLSearchParams): MyTaskFlag[] {
  return [...new Set(params.getAll('flag').filter((flag) => flag !== ''))] as MyTaskFlag[];
}

/** What no task is under the flags applied, any of them — "is overdue or is new since your last
 *  visit". */
function noneSaid(flags: readonly string[]): string {
  return flags.map((flag) => flagSaid(flag).none).join(' or ');
}

/** A flag's signal as a button's name — "Show 2 overdue DD calls". */
function showName(value: Nullable<MyTaskType>, flag: MyTaskFlag, count: number): string {
  const noun = NOUNS[value ?? 'all'][count === 1 ? 0 : 1];
  return `Show ${count.toLocaleString('en-US')} ${FLAG_SAID[flag].show(noun)}`;
}

/** The flags the compliance queue narrows by itself (`WorkItemFlags`). */
const WORK_ITEM_FLAGS: readonly string[] = ['overdue', 'due_today', 'new'] satisfies WorkItemFlag[];

function isWorkItemFlag(flag: string): flag is WorkItemFlag {
  return WORK_ITEM_FLAGS.includes(flag);
}

/** The flags applied, as the chips line's "Showing: overdue ✕ or new ✕" — a ✕ drops its flag. */
function FlagChips({ flags, onClear }: { flags: readonly string[]; onClear: (flag: string) => void }) {
  return (
    <SignalChips
      chips={flags.map((flag) => {
        const words = flagSaid(flag).chip;
        return { key: flag, words, clearName: `Show every task, not only ${words}`, onClear: () => onClear(flag) };
      })}
    />
  );
}

/** The address without one flag, from page 1. */
function withoutFlag(params: URLSearchParams, flag: string): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete('flag');
  for (const kept of flagsOf(params)) if (kept !== flag) next.append('flag', kept);
  return dropPaging(next);
}

/** The types as cards — All, then each type — each a link to `?type=`, which drops the rest of the
 *  query as a change of type always has. A card's signals are the read's, compact its first and
 *  those applied; All's are the overdue, due today and new tasks summed over the types, detailed
 *  only. Every signal is a toggle: on the open card its flag added to the query or dropped from it,
 *  on another that card's `?type=` with its flag alone. */
function TypeCards({
  type,
  flags,
  counts,
  end
}: {
  type: Nullable<string>;
  flags: readonly MyTaskFlag[];
  counts: Nullable<TaskCounts>;
  /** *Opens in*, at the cards' row end: the page has no toolbar of its own. */
  end?: React.ReactNode;
}) {
  const [variant] = useCardVariant();
  const [params, setParams] = useSearchParams();
  const all = counts ? Object.values(counts.counts).reduce((sum, n) => sum + n, 0) : null;
  const pressable = (
    value: Nullable<MyTaskType>,
    specs: readonly MyTaskSignalSpec[],
    found: IStateSignal[]
  ): IStateSignal[] =>
    found.map((signal) => {
      const spec = specs.find((one) => one.key === signal.key);
      if (!spec?.flag) return signal;
      const target = spec.flag;
      const open = type === value;
      const active = open && flags.includes(target);
      const onToggle = () => {
        if (active) setParams(withoutFlag(params, target));
        else if (open) {
          const next = new URLSearchParams(params);
          next.append('flag', target);
          setParams(dropPaging(next));
        } else setParams(new URLSearchParams({ ...(value !== null && { type: value }), flag: target }));
      };
      return { ...signal, active, onToggle, toggleName: showName(value, target, signal.count) };
    });
  const signalsFor = (value: MyTaskType): IStateSignal[] => {
    const specs = MY_TASK_SIGNALS[value] ?? [];
    const found = pressable(value, specs, signalsOf(counts?.signals?.[value] ?? {}, '', specs));
    return variant === 'detailed' ? found : found.filter((one, index) => index === 0 || one.active);
  };
  const summed = ALL_TASK_SIGNALS.flatMap((spec) => {
    const found = Object.values(counts?.signals ?? {}).flatMap((read) => signalsOf(read ?? {}, '', [spec]));
    return found.length > 0 ? [{ ...spec, count: found.reduce((sum, one) => sum + one.count, 0) }] : [];
  });
  const allSignals = variant === 'detailed' ? pressable(null, ALL_TASK_SIGNALS, summed) : [];
  const progress = Object.values(counts?.progress ?? {}).filter((one): one is IMyTaskProgress => !!one);
  const progressOf = (value: Nullable<MyTaskType>): IMyTaskProgress | undefined =>
    value !== null
      ? counts?.progress?.[value]
      : progress.length > 0
        ? progress.reduce((sum, one) => ({ done: sum.done + one.done, total: sum.total + one.total }))
        : undefined;
  const card = (value: Nullable<MyTaskType>, label: string, count: Nullable<number> | undefined) => ({
    key: value ?? 'all',
    to: { pathname: TEAM_PATHS.tasks, search: value === null ? '' : `?type=${value}` },
    current: type === value,
    label,
    // A count survey-api did not send is no 0.
    count: count?.toLocaleString('en-US') ?? '…',
    hint: HINTS[value ?? 'all'],
    signals: value === null ? allSignals : signalsFor(value),
    progress: progressOf(value),
    data: { 'data-type': value ?? 'all' },
    countData: { 'data-count': count?.toString() ?? '' }
  });
  return (
    <StateCards
      label="Task type"
      compact
      end={end}
      cards={[
        card(null, 'All', all),
        ...MY_TASK_TYPES.map((entry) => card(entry.value, entry.label, counts?.counts[entry.value]))
      ]}
    />
  );
}

/** Every task of one type, or of all of them — those raising any flag applied: survey-api's
 *  merged page, its counts handed up. */
function AllTasks({
  type,
  flags,
  since,
  reread,
  onCounts,
  onReading,
  controlsEnd,
  barSlots
}: {
  type: Nullable<string>;
  flags: readonly MyTaskFlag[];
  since: Nullable<string>;
  reread: number;
  onCounts: (counts: TaskCounts) => void;
  onReading: (reading: boolean) => void;
  /** Receives the element drawn where the list's controls end — the compact bar's cue. */
  controlsEnd: (element: Nullable<HTMLElement>) => void;
  /** The compact bar's places for the list's parts. */
  barSlots: IBarSlots;
}) {
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  // The window — the page and the rows *Load more* added — is the URL's.
  const paging = useWorkPaging();
  const { page, perPage, pages } = paging;
  const asked = `${type ?? ''}|${page}|${perPage}|${pages}`;
  const read = useAdminData<{ asked: string; page: IMyTasksPage }>(
    async (signal) => {
      try {
        const answer = await readItemPages(
          (at, pageSize) =>
            workItemsApi.myTasks(
              { type: (type ?? undefined) as MyTaskType | undefined, page: at, pageSize, since, flag: flags },
              signal
            ),
          page,
          perPage,
          pages,
          WORK_ITEMS_MAX_PAGE
        );
        return { asked, page: answer };
      } catch (readError) {
        throw new Error(refusalMessage(readError, 'Could not load this page'));
      }
    },
    [asked, reread]
  );
  const { error } = read;
  React.useEffect(() => onReading(read.reading), [read.reading, onReading]);
  // A result is drawn only under the query it answers: a failed read under another page draws none.
  const data = error && read.data?.asked !== asked ? null : (read.data?.page ?? null);
  React.useEffect(() => {
    if (data) onCounts(data);
  }, [data, onCounts]);
  // A press for the page already asked for — after its read failed — asks again.
  const turnTo = (target: number) =>
    target === page ? void read.reload() : paging.setPage(target);

  const tableState = tableStateOf(
    data,
    error,
    flags.length > 0
      ? `No task of yours ${noneSaid(flags)}.`
      : type === null
        ? 'Nothing is assigned to you.'
        : 'No task of this type is assigned to you.',
    turnTo
  );

  // No search and no quick filter: the flags pressed are the list's one row of filters applied.
  const flagChip = <FlagChips flags={flags} onClear={(flag) => setParams(withoutFlag(params, flag))} />;
  const chips: ICompactChip[] = flags.length > 0 ? [{ key: 'flags', chip: flagChip }] : [];
  const clearAll = () => setParams(new URLSearchParams(type === null ? {} : { type }));

  return (
    <>
      <CompactPortal to={barSlots.applied}>
        {chips.length > 0 && <CompactChipLine chips={chips} onClearAll={clearAll} />}
      </CompactPortal>
      {chips.length > 0 && <CompactChipLine label="Filters" chips={chips} onClearAll={clearAll} />}
      {/* No toolbar here: the controls end where the cards or the chip line do, one gap above this
          block — the sentinel sits there, taking no row of the page's grid. */}
      <div className="relative grid min-w-0 gap-3">
        <div
          ref={controlsEnd}
          aria-hidden
          data-controls-end=""
          className="pointer-events-none absolute -top-3 h-0 w-full"
        />
        {error && data !== null && (
          <LoadError message={`Could not refresh — showing the list as last read. ${error}`} />
        )}
        <MyTasksTable
          rows={data?.items ?? []}
          state={tableState}
          search={location.search}
          grouped={type === null}
          counts={data?.counts ?? null}
          flags={flags}
          stickyTop={STICKY_TABLE_TOP}
        />
        {data && (
          <WorkPager {...paging.pager} total={data.total} busy={read.reading} onPage={turnTo} sticky />
        )}
      </div>
    </>
  );
}


/** Either list's table state: reading, its read's failure, or empty — past the end of a list
 *  that is not, else `empty` — over the page drawn. */
function tableStateOf(
  page: Nullable<IPage<unknown>>,
  error: Nullable<string>,
  empty: string,
  onPage: (target: number) => void
): DataTableState | undefined {
  if (page === null) {
    return error ? { kind: 'error', content: <LoadError message={error} /> } : { kind: 'loading' };
  }
  if (page.items.length > 0) return undefined;
  return {
    kind: 'empty',
    content:
      page.total > 0 ? (
        <PastTheEnd
          page={page.page}
          pageSize={page.pageSize}
          total={page.total}
          counted={['task', 'tasks']}
          onPage={onPage}
        />
      ) : (
        empty
      )
  };
}

/** The compliance work items alone — the old My tasks, its filters and bulk decisions kept, and
 *  under flags those raising any one (the queue's own repeated `flag=`, said in the chips line). The counts are one
 *  read of the merge, since this list reads the queue itself: after a first visit,
 *  of the compliance items new since it (`flag=new`), which mark their rows; the due day marks the
 *  rest, as survey-api's flags read it — before today's UTC day overdue, on it due today. */
function ComplianceTasks({
  flags,
  since,
  reread,
  onCounts,
  onReading,
  controlsEnd,
  barSlots
}: {
  flags: readonly WorkItemFlag[];
  since: Nullable<string>;
  reread: number;
  onCounts: (counts: TaskCounts) => void;
  onReading: (reading: boolean) => void;
  controlsEnd: (element: Nullable<HTMLElement>) => void;
  barSlots: IBarSlots;
}) {
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const survey = params.get('survey');
  const triggers = params.getAll('trigger');

  // The window — the page and the rows *Load more* added — is the URL's.
  const paging = useWorkPaging();
  const { perPage, pages } = paging;
  const query = myTasksQueryOf(params, since, undefined, perPage);
  const asked = JSON.stringify(query);
  const readKey = `${asked}|${pages}`;
  const {
    data,
    error,
    reload,
    reading: listReading
  } = useAdminData<{ asked: string; page: IPage<IWorkItemRow> }>(
    async (signal) => {
      try {
        const page = await readItemPages(
          (at, pageSize) => workItemsApi.listWorkItems({ ...query, page: at, pageSize }, signal),
          query.page ?? 1,
          perPage,
          pages,
          WORK_ITEMS_MAX_PAGE
        );
        return { asked: readKey, page };
      } catch (listError) {
        throw new Error(refusalMessage(listError, 'Could not load this page'));
      }
    },
    [readKey, reread]
  );
  // Counted again whenever the list is (a decision moves them), never before it is.
  const counted = useAdminData(
    async (signal) =>
      data === null
        ? null
        : workItemsApi.myTasks(
            since === null
              ? { pageSize: 1 }
              : { type: 'compliance', flag: ['new'], since, pageSize: WORK_ITEMS_MAX_PAGE },
            signal
          ),
    [data]
  );
  const fresh = React.useMemo(
    () => new Set(since === null ? [] : (counted.data?.items.map((task) => task.workItem?.id) ?? [])),
    [counted.data, since]
  );
  const today = new Date().toISOString().slice(0, 10);
  const marks = (row: IWorkItemRow) => ({
    overdue: row.dueOn !== null && row.dueOn < today,
    dueToday: row.dueOn === today,
    isNew: fresh.has(row.id)
  });
  React.useEffect(() => {
    if (counted.data) onCounts(counted.data);
  }, [counted.data, onCounts]);
  const reading = listReading || counted.reading;
  React.useEffect(() => onReading(reading), [reading, onReading]);
  const shown = error && data?.asked !== readKey ? null : data;
  const selection = useQuerySelection(asked, shown?.page.items ?? [], (row) => row.id);

  /** A filter change starts at page 1; the type stays. */
  const update = (change: (next: URLSearchParams) => void) => {
    const next = new URLSearchParams(params);
    change(next);
    setParams(dropPaging(next));
  };
  const toggleTrigger = (trigger: string, on: boolean) =>
    update((next) => toggleRepeated(next, 'trigger', WORK_ITEM_TRIGGERS, trigger, on));
  const turnTo = paging.setPage;
  // A survey the mirror lacks, named by the URL, is still a position of its own.
  const queues = [...WORK_ITEM_QUEUES, ...(survey && !isWorkItemQueue(survey) ? [survey] : [])];

  const narrowed = [...params.keys()].some((name) => !['page', 'size', 'type'].includes(name));
  const tableState = tableStateOf(
    shown?.page ?? null,
    error,
    flags.length > 0
      ? `No compliance work item of yours ${noneSaid(flags)}.`
      : narrowed
        ? 'No task of yours matches this view.'
        : 'No work item is assigned to you.',
    turnTo
  );

  const chooseSurvey = (value: Nullable<string>) =>
    update((next) => {
      next.delete('survey');
      if (value !== null) next.set('survey', value);
    });
  const triggerSaid = triggers.map((trigger) => asSpelled(triggerLabel(trigger))).join(', ');
  // The queue's quick filters, drawn in the toolbar by name and again in the compact bar's *Filters*.
  const quickFilters = (
    <>
      <SelectChip
        label="Survey"
        any="Every queue"
        value={survey}
        options={queues.map((code) => ({ value: code, label: code }))}
        onChoose={chooseSurvey}
      />
      <FilterChip label="Raised by" summary={triggerSaid}>
        <fieldset aria-label="Raised by" className="grid gap-2">
          {withUnknown(WORK_ITEM_TRIGGERS, triggers).map((trigger) => (
            <label key={trigger} className="flex items-center gap-2">
              <Checkbox
                checked={triggers.includes(trigger)}
                onCheckedChange={(next) => toggleTrigger(trigger, next === true)}
              />
              {asSpelled(triggerLabel(trigger))}
            </label>
          ))}
        </fieldset>
      </FilterChip>
    </>
  );
  // Every filter applied, as a chip with its value — under the toolbar and on the compact bar's
  // second row: the flags pressed first ("Showing: …"), then each quick filter set, whose press
  // opens its control (`open`, by its name) and whose ✕ clears it.
  const toolbar = useWorkToolbar();
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  const openFilters = () => {
    setFiltersOpen(true);
    return true as const;
  };
  const quickChipsWith = (open: (label: string) => true): ICompactChip[] => {
    const said: [string, string, Nullable<string>, () => void][] = [
      ['survey', 'Survey', survey === null ? null : asSpelled(survey), () => chooseSurvey(null)],
      ['trigger', 'Raised by', triggerSaid || null, () => update((next) => next.delete('trigger'))]
    ];
    return said.flatMap(([key, label, value, onRemove]) =>
      value === null
        ? []
        : [
            {
              key,
              chip: (
                <ParamChip
                  words={`${label}: ${value}`}
                  body={null}
                  onPress={() => open(label)}
                  onRemove={onRemove}
                />
              )
            }
          ]
    );
  };
  const flagChip = <FlagChips flags={flags} onClear={(flag) => setParams(withoutFlag(params, flag))} />;
  const appliedChipsWith = (open: (label: string) => true): ICompactChip[] => [
    ...(flags.length > 0 ? [{ key: 'flags', chip: flagChip }] : []),
    ...quickChipsWith(open)
  ];
  const quickCount = quickChipsWith(openFilters).length;
  const appliedChips = appliedChipsWith(openFilters);
  const pageChips = appliedChipsWith(toolbar.openQuick);
  const clearAll = () => setParams(new URLSearchParams({ type: 'compliance' }));

  return (
    <>
      <CompactPortal to={barSlots.filters}>
        <CompactFilters count={quickCount} open={filtersOpen} onOpenChange={setFiltersOpen}>
          {quickFilters}
        </CompactFilters>
      </CompactPortal>
      <CompactPortal to={barSlots.applied}>
        {appliedChips.length > 0 && <CompactChipLine chips={appliedChips} onClearAll={clearAll} />}
      </CompactPortal>
      <WorkControls end={controlsEnd}>
        <WorkToolbar toolbar={toolbar} quick={quickFilters} quickCount={quickCount} />
        {pageChips.length > 0 && (
          <CompactChipLine label="Filters" chips={pageChips} onClearAll={clearAll} />
        )}
      </WorkControls>

      {error && shown !== null && (
        <LoadError message={`Could not refresh — showing the list as last read. ${error}`} />
      )}

      <BulkDecisions
        asked={asked}
        rows={selection.rows}
        onSelect={selection.onSelect}
        onDecided={() => void reload()}
        stickyTop={STICKY_BAR_TOP}
      />

      <WorkItemsTable
        rows={shown?.page.items ?? []}
        state={tableState}
        search={location.search}
        itemPath={(id) => myTaskPath(id)}
        queueColumn
        pickerId="my-tasks"
        pickerNote={STAFF_FIELDS_NOTE}
        selected={selection.selected}
        onSelect={selection.onSelect}
        marks={marks}
        stickyTop={STICKY_TABLE_TOP}
      />

      {shown && (
        <WorkPager {...paging.pager} total={shown.page.total} busy={listReading} sticky />
      )}
    </>
  );
}

/** Compliance queue's query — the fixed part survey-api is asked for, and the URL's two filters
 *  and flags, `since` sent with `new` alone; the pane beside a task reads it too. */
function myTasksQueryOf(
  params: URLSearchParams,
  since: Nullable<string>,
  page = pageOf(params.get('page')),
  pageSize?: number
): IWorkItemQuery {
  const survey = params.get('survey');
  const flag = flagsOf(params);
  return {
    ...MY_TASKS_QUERY,
    survey: survey === null ? undefined : [survey],
    trigger: params.getAll('trigger'),
    flag,
    ...(flag.includes('new') && since !== null && { since }),
    page,
    pageSize
  };
}

/** What makes the work items My tasks' (R9). */
const MY_TASKS_QUERY: IWorkItemQuery = {
  assignee: 'me',
  state: [...UNCLOSED_STATES],
  sort: 'due_on'
};

/** My tasks and a task, as one area: the list, or a task with the list beside it — a work item,
 *  a number check (`checkKey`), a DD meeting (`interviewId`) or a Lists row (`rowKey`), the pane
 *  listing every type the list does. */
export function AdminMyTasksArea() {
  return (
    <ListArea
      param={['workItemId', 'rowKey', 'checkKey', 'interviewId']}
      list={TEAM_PATHS.tasks}
      paneTitle="My tasks"
      pane={(search, selected) => <MyTasksListPane search={search} selected={selected} />}
    />
  );
}

/** The pane's list: the list's own read — the merge under its type, or the queue's under
 *  Compliance queue's filters and flag — so the pane and the list are one query. */
function MyTasksListPane({ search, selected }: { search: string; selected: string }) {
  // The list's own Per page and window, so the URL's page is the same rows beside a task.
  const [perPage] = usePerPage();
  const { since } = useLastVisit();
  const { shown, pager, status } = usePaneRead<IMyTask>(
    search,
    'Nothing is assigned to you.',
    (params, page, signal) => {
      const type = params.get('type');
      const flags = flagsOf(params);
      const readOne = async (at: number, pageSize: number): Promise<IPage<IMyTask>> => {
        if (type === 'compliance' && flags.every(isWorkItemFlag)) {
          const items = await workItemsApi.listWorkItems(myTasksQueryOf(params, since, at, pageSize), signal);
          return {
            ...items,
            items: items.items.map((item) => ({
              type: 'compliance' as const,
              workItem: item,
              row: null
            }))
          };
        }
        return workItemsApi.myTasks(
          { type: (type ?? undefined) as MyTaskType | undefined, page: at, pageSize, since, flag: flags },
          signal
        );
      };
      const window = windowOf(params, perPage);
      return readItemPages(readOne, page, perPage, page === window.page ? window.pages : 1, WORK_ITEMS_MAX_PAGE);
    }
  );
  return (
    <SplitList
      label="My tasks"
      selected={selected}
      items={
        shown?.items.map((task) => ({
          key: taskKey(task),
          to: taskPath(task),
          state: { search, list: true } satisfies ICardLinkState,
          primary: taskName(task),
          secondary: [taskTitle(task), taskSurvey(task)].filter(Boolean).join(' · '),
          trailing: <TaskState task={task} />
        })) ?? null
      }
      status={status}
      footer={pager}
    />
  );
}
